import { useState, useEffect } from "react"
import { CheckCircle, XCircle, Calendar, Download, Eye, Filter, ChevronDown } from "lucide-react"
import { qualityControlApi } from "../services/quality-control.service"

interface QualityControlMetrics {
  optIn: {
    total: number
    success: number
    failed: number
    successRate: number
    failureRate: number
  }
  intake: {
    total: number
    success: number
    failed: number
    successRate: number
    failureRate: number
  }
}

interface OrganizationHierarchy {
  id: string
  name: string
  type: string
  parent?: OrganizationHierarchy | null
}

interface SubmissionRecord {
  id: string
  enrollmentSessionId: string
  submissionType: "OPT_IN" | "INTAKE"
  status: "SUCCESS" | "FAILED"
  timestamp: string
  clientIp: string | null
  pdfUrl: string | null
  agent: {
    id: string
    firstName: string
    lastName: string
    email: string
    agentProfile?: {
      agentCode: string
    } | null
    organization?: {
      id: string
      name: string
    } | null
    hrOrganization?: OrganizationHierarchy | null
  }
  campaign?: {
    id: string
    name: string
  } | null
}

interface QualityControlProps {
  campaignId?: string
  campaignName?: string
}

export default function QualityControl({ campaignId, campaignName }: QualityControlProps) {
  const [dateRange, setDateRange] = useState<'day' | 'week' | 'month' | 'year' | 'all'>('month')
  const [metrics, setMetrics] = useState<QualityControlMetrics | null>(null)
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([])
  const [allSubmissions, setAllSubmissions] = useState<SubmissionRecord[]>([]) // Store all submissions for client-side filtering
  const [loadingMetrics, setLoadingMetrics] = useState(true)
  const [loadingSubmissions, setLoadingSubmissions] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const itemsPerPage = 10

  // Filters
  const [filters, setFilters] = useState({
    emLevel: '',
    dmLevel: '',
    cmLevel: '',
    bmLevel: '',
    amLevel: '',
    submissionType: '',
    status: ''
  })

  // Fetch metrics
  useEffect(() => {
    if (!campaignId) {
      setMetrics(null)
      setLoadingMetrics(false)
      return
    }

    const fetchMetrics = async () => {
      try {
        setLoadingMetrics(true)
        const data = await qualityControlApi.getMetrics({ 
          dateRange,
          campaignId 
        })
        setMetrics(data)
      } catch (error) {
        console.error('Error fetching metrics:', error)
        setMetrics(null)
      } finally {
        setLoadingMetrics(false)
      }
    }

    fetchMetrics()
  }, [dateRange, campaignId])

  // Fetch submissions
  useEffect(() => {
    if (!campaignId) {
      setSubmissions([])
      setLoadingSubmissions(false)
      return
    }

    const fetchSubmissions = async () => {
      try {
        setLoadingSubmissions(true)
        // Fetch all submissions (without pagination) when filters are applied, otherwise use pagination
        const hasHierarchyFilters = filters.emLevel || filters.dmLevel || filters.cmLevel || filters.bmLevel || filters.amLevel
        
        if (hasHierarchyFilters) {
          // Fetch all submissions for client-side filtering
          const data = await qualityControlApi.getSubmissions({
            page: 1,
            limit: 10000, // Large limit to get all records
            campaignId,
            dateRange, // Include dateRange filter
            // Exclude hierarchy filters from API call
            submissionType: filters.submissionType || undefined,
            status: filters.status || undefined
          })
          
          // Client-side filtering by organization hierarchy levels (E, D, C, B, A)
          let filteredSubmissions = data.submissions.filter((submission: SubmissionRecord) => {
            const hierarchy = getOrganizationHierarchy(submission.agent.hrOrganization)
            
            if (filters.emLevel && hierarchy.e && !hierarchy.e.toLowerCase().includes(filters.emLevel.toLowerCase())) {
              return false
            }
            if (filters.dmLevel && hierarchy.d && !hierarchy.d.toLowerCase().includes(filters.dmLevel.toLowerCase())) {
              return false
            }
            if (filters.cmLevel && hierarchy.c && !hierarchy.c.toLowerCase().includes(filters.cmLevel.toLowerCase())) {
              return false
            }
            if (filters.bmLevel && hierarchy.b && !hierarchy.b.toLowerCase().includes(filters.bmLevel.toLowerCase())) {
              return false
            }
            if (filters.amLevel && hierarchy.a && !hierarchy.a.toLowerCase().includes(filters.amLevel.toLowerCase())) {
              return false
            }
            return true
          })
          
          setAllSubmissions(filteredSubmissions)
          
          // Apply pagination to filtered results
          const startIndex = (currentPage - 1) * itemsPerPage
          const endIndex = startIndex + itemsPerPage
          const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex)
          setSubmissions(paginatedSubmissions)
          setTotalPages(Math.ceil(filteredSubmissions.length / itemsPerPage))
        } else {
          // Use server-side pagination when no hierarchy filters
          const data = await qualityControlApi.getSubmissions({
            page: currentPage,
            limit: itemsPerPage,
            campaignId,
            dateRange, // Include dateRange filter (separate from filters object)
            ...filters
          })
          
          setSubmissions(data.submissions)
          setAllSubmissions(data.submissions)
          setTotalPages(data.totalPages)
        }
      } catch (error) {
        console.error('Error fetching submissions:', error)
        setSubmissions([])
        setAllSubmissions([])
      } finally {
        setLoadingSubmissions(false)
      }
    }

    fetchSubmissions()
  }, [currentPage, filters, campaignId, dateRange])
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.emLevel, filters.dmLevel, filters.cmLevel, filters.bmLevel, filters.amLevel, filters.submissionType, filters.status])

  // Show message if no campaign is selected (after hooks)
  if (!campaignId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="bg-yellow-50 border border-yellow-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Selection Required</h3>
          <p className="text-gray-600">
            Please select a campaign to view quality control metrics and submissions.
          </p>
        </div>
      </div>
    )
  }


  // Helper function to extract organization hierarchy levels (E, D, C, B, A)
  const getOrganizationHierarchy = (org: OrganizationHierarchy | null | undefined) => {
    if (!org) {
      return { e: null, d: null, c: null, b: null, a: null }
    }
    
    const hierarchy: { e: string | null, d: string | null, c: string | null, b: string | null, a: string | null } = {
      e: null,
      d: null,
      c: null,
      b: null,
      a: null
    }
    
    // Traverse up the hierarchy to find all levels
    let current: OrganizationHierarchy | null | undefined = org
    const visited = new Set<string>()
    
    while (current && !visited.has(current.id)) {
      visited.add(current.id)
      
      // Map organization types to hierarchy levels
      // E = EM, D = DM, C = CM, B = BM, A = AM
      // Check both the type field and ensure we're setting the right level
      const orgType = current.type?.toUpperCase()
      
      if (orgType === 'EM' && !hierarchy.e) {
        hierarchy.e = current.name
      } else if (orgType === 'DM' && !hierarchy.d) {
        hierarchy.d = current.name
      } else if (orgType === 'CM' && !hierarchy.c) {
        hierarchy.c = current.name
      } else if (orgType === 'BM' && !hierarchy.b) {
        hierarchy.b = current.name
      } else if (orgType === 'AM' && !hierarchy.a) {
        hierarchy.a = current.name
      }
      
      current = current.parent
    }
    
    return hierarchy
  }

  const handlePdfView = async (submissionId: string) => {
    try {
      const pdfData = await qualityControlApi.getSubmissionPdf(submissionId)
      
      // Fetch the PDF as a blob to display in browser instead of downloading
      const response = await fetch(pdfData.pdfUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`)
      }
      
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      
      // Open in new tab with blob URL
      const newWindow = window.open(blobUrl, '_blank')
      
      // Clean up blob URL after a delay to allow the browser to load it
      if (newWindow) {
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl)
        }, 1000)
      } else {
        // If popup was blocked, revoke immediately
        window.URL.revokeObjectURL(blobUrl)
        alert('Please allow popups to view PDFs in a new window, or use the download button.')
      }
    } catch (error) {
      console.error('Error viewing PDF:', error)
      alert('Failed to open PDF. Please try again or contact support.')
    }
  }

  const handlePdfDownload = async (submissionId: string) => {
    try {
      const pdfData = await qualityControlApi.getSubmissionPdf(submissionId)
      
      // Fetch the PDF as a blob to handle CORS and signed URL issues
      const response = await fetch(pdfData.pdfUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`)
      }
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = url
      link.download = `submission_${submissionId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up the object URL
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      alert('Failed to download PDF. Please try again or contact support.')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quality Control</h1>
          {campaignName && (
            <p className="text-sm text-gray-600 mt-1">Campaign: {campaignName}</p>
          )}
          <p className="text-xs text-gray-500 mt-1 italic">
            Showing fully completed applications only
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 1st Section: OPT IN FORM Metrics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">OPT IN FORM</h2>
          {loadingMetrics ? (
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ) : metrics ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <span className="text-sm font-medium text-gray-700">TOTAL SUBMISSIONS (NUMBER)</span>
                <span className="text-2xl font-bold text-gray-900">{metrics.optIn.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">OPT IN FORM SUCCESS</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-green-600">{metrics.optIn.success || 0}</div>
                  <div className="text-xs text-gray-500">({(metrics.optIn.successRate || 0).toFixed(1)}%)</div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-medium text-gray-700">OPT IN FORM FAILURE</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-red-600">{metrics.optIn.failed || 0}</div>
                  <div className="text-xs text-gray-500">({(metrics.optIn.failureRate || 0).toFixed(1)}%)</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No metrics available</p>
            </div>
          )}
        </div>

        {/* 2nd Section: INTAKE FORM Metrics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">INTAKE FORM</h2>
          {loadingMetrics ? (
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ) : metrics ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <span className="text-sm font-medium text-gray-700">TOTAL SUBMISSIONS (NUMBER)</span>
                <span className="text-2xl font-bold text-gray-900">{metrics.intake.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">INTAKE FORM SUCCESS</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-green-600">{metrics.intake.success || 0}</div>
                  <div className="text-xs text-gray-500">({(metrics.intake.successRate || 0).toFixed(1)}%)</div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-medium text-gray-700">INTAKE FORM FAILURE</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-red-600">{metrics.intake.failed || 0}</div>
                  <div className="text-xs text-gray-500">({(metrics.intake.failureRate || 0).toFixed(1)}%)</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No metrics available</p>
            </div>
          )}
        </div>
      </div>

      {/* Submissions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Fully Completed Applications</h2>
              <p className="text-xs text-gray-500 mt-1">Only showing submissions from fully completed enrollment sessions</p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">E (EM Level)</label>
                <input
                  type="text"
                  value={filters.emLevel}
                  onChange={(e) => setFilters({ ...filters, emLevel: e.target.value })}
                  placeholder="Filter by E"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">D (DM Level)</label>
                <input
                  type="text"
                  value={filters.dmLevel}
                  onChange={(e) => setFilters({ ...filters, dmLevel: e.target.value })}
                  placeholder="Filter by D"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">C (CM Level)</label>
                <input
                  type="text"
                  value={filters.cmLevel}
                  onChange={(e) => setFilters({ ...filters, cmLevel: e.target.value })}
                  placeholder="Filter by C"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">B (BM Level)</label>
                <input
                  type="text"
                  value={filters.bmLevel}
                  onChange={(e) => setFilters({ ...filters, bmLevel: e.target.value })}
                  placeholder="Filter by B"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">A (AM Level)</label>
                <input
                  type="text"
                  value={filters.amLevel}
                  onChange={(e) => setFilters({ ...filters, amLevel: e.target.value })}
                  placeholder="Filter by A"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={filters.submissionType}
                  onChange={(e) => setFilters({ ...filters, submissionType: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="OPT_IN">Opt-In</option>
                  <option value="INTAKE">Intake</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="SUCCESS">Success</option>
                  <option value="FAILED">Failed</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">D</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">C</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">B</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">A</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">View PDF</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Download PDF</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingSubmissions ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-sm text-gray-500">
                    Loading submissions...
                  </td>
                </tr>
              ) : submissions.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-sm text-gray-500">
                    No fully completed submissions found
                  </td>
                </tr>
              ) : (
                submissions.map((submission) => {
                  const hierarchy = getOrganizationHierarchy(submission.agent.hrOrganization)
                  return (
                  <tr key={submission.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-600 font-medium min-w-[100px]">
                      {hierarchy.e || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-600 font-medium min-w-[100px]">
                      {hierarchy.d || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-600 font-medium min-w-[100px]">
                      {hierarchy.c || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-600 font-medium min-w-[100px]">
                      {hierarchy.b || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-600 font-medium min-w-[100px]">
                      {hierarchy.a || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.agent.firstName} {submission.agent.lastName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.agent.agentProfile?.agentCode || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(submission.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handlePdfView(submission.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View PDF"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handlePdfDownload(submission.id)}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loadingSubmissions && (submissions.length > 0 || allSubmissions.length > 0) && (
          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, allSubmissions.length || submissions.length)} of {allSubmissions.length || submissions.length} submissions
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                      let pageNum: number
                      if (totalPages <= 10) {
                        pageNum = i + 1
                      } else if (currentPage <= 5) {
                        pageNum = i + 1
                      } else if (currentPage >= totalPages - 4) {
                        pageNum = totalPages - 9 + i
                      } else {
                        pageNum = currentPage - 4 + i
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
